// src/context/DataContext.js
import React, { createContext, useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { s3ImageUrl } from "../utils/s3Url";
import { fetchDashboardYearParts } from "../utils/dashboardApi";
import { buildDashboardYearBlockFromTerritories } from "../utils/dashboardRollup";
import { API_BASE } from "../config/api";

export const DataContext = createContext();

export function DataProvider({ children }) {
  const { user, loading: authLoading } = useAuth();

  const [data, setData] = useState({
    customers:     [],
    orders:        [],
    products:      [],
    territories:   [],
    promotions:    [],
    resources:     [],
    users:         [],
    userProfile:   null,
    brands:        [],
    tasks:         [],
    alerts:        [],
    budgets:       [],
    dashboard:     {},

    distributions:  [],
    categories:     [],
    unitsOfMeasure: [],
    banners:        [],
    regions:        [],
    storeType:      [],

    settings: {
      systemAlerts:   [],
      budgetTargets:  [],
      managedBrands:  [],
      productTypes:   [],
      managedRegions: [],
      storeType:      [],
    },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setData(d => ({ ...d, userProfile: null }));
      setLoading(false);
      return;
    }

    const sub = user.username;
    (async () => {
      try {
        setLoading(true);

        // 1) Profile
        const profileRes = await fetch(
          `${API_BASE}/users/${sub}`
        );
        if (!profileRes.ok) throw new Error("Profile fetch failed");
        const profile = await profileRes.json();
        profile.sub = sub;
        profile.image = `${s3ImageUrl("users", profile.id, ".png")}?cb=${Date.now()}`;

        // 2) Dashboard
        const now = new Date();
        const month = now.getMonth() + 1; // Jan=1…Dec=12
        const financialYear = now.getFullYear() + (month >= 4 ? 1 : 0);

        const { territories, topCustomers } = await fetchDashboardYearParts({
          yearEnd: financialYear,
          sub,
        });

        // 3) Initial misc data
        const initialRes = await fetch(
          `${API_BASE}/initial/${sub}`
        );
        if (!initialRes.ok) throw new Error("Initial data fetch failed");
        const initialData = await initialRes.json();
        const settingsObj = Array.isArray(initialData.settings)
          ? initialData.settings[0] || {}
          : {};

        const yearBlock = buildDashboardYearBlockFromTerritories(territories);

        // 5) Set all DataContext state, embedding topCustomers under FY
        setData(prev => ({
          ...prev,
          products:       initialData.products       || [],
          territories:    initialData.territories    || [],
          promotions:     initialData.promotions     || [],
          resources:      initialData.resources      || [],
          users:          initialData.users          || [],
          brands:         initialData.brands         || [],
          tasks:          initialData.tasks          || [],
          alerts:         initialData.alerts         || [],
          budgets:        initialData.budgets        || [],
          distributions:  initialData.distributions  || [],
          categories:     initialData.categories     || [],
          unitsOfMeasure: initialData.unitsOfMeasure || [],
          banners:        initialData.banners        || [],
          regions:        initialData.regions        || [],
          storeType:      initialData.storeType      || [],
          userProfile:    profile,
          dashboard: {
            ...prev.dashboard,
            [financialYear]: {
              ...yearBlock,
              topCustomers
            }
          },
          settings: {
            systemAlerts:   settingsObj.systemAlerts   || [],
            budgetTargets:  settingsObj.budgetTargets  || [],
            managedBrands:  settingsObj.managedBrands  || [],
            productTypes:   settingsObj.productTypes   || [],
            managedRegions: settingsObj.managedRegions || [],
            storeType:      settingsObj.storeType      || [],
          },
        }));
        setLoading(false);

        // 6) Background fetch customer orders
        const custOrdersRes = await fetch(
          `${API_BASE}/initialcustomerorders/${sub}`
        );
        if (custOrdersRes.ok) {
          const custOrdersData = await custOrdersRes.json();
          const allOrders = (custOrdersData.customers || [])
            .flatMap(c => c.orderHistory || []);
          setData(prev => ({
            ...prev,
            customers: custOrdersData.customers || [],
            orders:    allOrders,
          }));
        }
      } catch (err) {
        console.error("DataContext load error:", err);
        setLoading(false);
      }
    })();
  }, [user, authLoading]);

  if (authLoading) return null;

  return (
    <DataContext.Provider value={{ data, setData, loading }}>
      {children}
    </DataContext.Provider>
  );
}
